export const SUPPORT_FIELD_SOURCE_TYPES = [
  'default_requester',
  'default_subject',
  'default_source',
  'default_status',
  'default_priority',
  'default_ticket_type',
  'default_group',
  'default_agent',
  'default_internal_group',
  'default_internal_agent',
  'default_description',
  'default_company',
  'default_source_info',
  'custom_text',
  'custom_paragraph',
  'custom_dropdown',
  'nested_field',
] as const;

export type SupportFieldSourceType = (typeof SUPPORT_FIELD_SOURCE_TYPES)[number];

export const TICKET_FIELD_RENDERER_TYPES = [
  'single_line_text',
  'multi_line_text',
  'dropdown',
  'checkbox',
  'radio',
  'multiselect',
  'lookup',
  'dependent',
  'date',
  'number',
  'decimal',
] as const;

export type TicketFieldRendererType = (typeof TICKET_FIELD_RENDERER_TYPES)[number];

export const TICKET_OPTION_SOURCE_TYPES = [
  'STATIC',
  'USERS',
  'AGENTS',
  'COUNTRIES',
  'GROUPS',
  'ROLE',
  'CUSTOM',
] as const;

export type TicketOptionSourceType = (typeof TICKET_OPTION_SOURCE_TYPES)[number];

export function optionSourceTypeForSourceType(sourceType: SupportFieldSourceType): TicketOptionSourceType {
  if (sourceType === 'default_requester') return 'USERS';
  if (sourceType === 'default_agent' || sourceType === 'default_internal_agent') return 'AGENTS';
  if (sourceType === 'default_group' || sourceType === 'default_internal_group') return 'GROUPS';
  if (sourceType === 'custom_dropdown') return 'CUSTOM';
  return 'STATIC';
}

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | readonly JsonValue[] | { readonly [key: string]: JsonValue };

export type SupportChoiceArray = readonly JsonPrimitive[];
export type SupportChoiceMap = Readonly<Record<string, JsonPrimitive | SupportChoiceArray>>;
export interface SupportNestedChoiceTree {
  readonly [key: string]: JsonPrimitive | SupportChoiceArray | SupportNestedChoiceTree;
}
export type SupportChoices = SupportChoiceArray | SupportChoiceMap | SupportNestedChoiceTree;

export interface SupportNestedTicketFieldRecord {
  id: string | number;
  name: string;
  description: string | null;
  label: string;
  label_in_portal?: string | null;
  level: number;
  ticket_field_id: string | number;
  created_at: string;
  updated_at: string;
}

export interface SupportTicketFieldRecord {
  id: string | number;
  name: string;
  label: string;
  description: string | null;
  position: number;
  required_for_closure: boolean;
  required_for_agents: boolean;
  type: SupportFieldSourceType;
  default: boolean;
  customers_can_edit: boolean;
  customers_can_filter: boolean;
  label_for_customers?: string | null;
  required_for_customers: boolean;
  displayed_to_customers: boolean;
  created_at: string;
  updated_at: string;
  choices?: SupportChoices;
  nested_ticket_fields?: readonly SupportNestedTicketFieldRecord[];
  portal_cc?: boolean;
  portal_cc_to?: string | null;
  [key: string]: JsonValue | readonly SupportNestedTicketFieldRecord[] | undefined;
}

export interface TicketFieldDependency {
  id: string;
  externalId: string | null;
  fieldKey: string;
  parentFieldKey: string;
  parentFieldId: string;
  childFieldId: string;
  label: string;
  labelForCustomers: string | null;
  description: string | null;
  level: number;
  optionMappings: readonly TicketFieldOptionMapping[];
}

export interface TicketFieldOptionMapping {
  id?: string;
  parentOptionId: string;
  childOptionId: string;
}

export interface NormalizedTicketFieldOption {
  id: string;
  label: string;
  value: string;
  externalId?: string;
  active: boolean;
}

export interface TicketFieldDefinition {
  id: string;
  externalId: string;
  fieldKey: string;
  label: string;
  labelForCustomers: string | null;
  description: string | null;
  position: number;
  sourceOrder: number;
  sourceType: SupportFieldSourceType;
  rendererType: TicketFieldRendererType;
  optionSourceType: TicketOptionSourceType;
  optionSourceId: string | null;
  requiredForAgents: boolean;
  requiredForCustomers: boolean;
  requiredForClosure: boolean;
  displayedToCustomers: boolean;
  customersCanEdit: boolean;
  customersCanFilter: boolean;
  isDefault: boolean;
  isActive?: boolean;
  rawChoices: readonly NormalizedTicketFieldOption[] | null;
  dependsOnFieldId?: string | null;
  dependsOnFieldKey?: string | null;
  dependencyId?: string | null;
  parentFieldId?: string | null;
  dependencies: readonly TicketFieldDependency[];
}

export interface TicketFormFieldReference {
  fieldKey: string;
  displayOrder: number;
  enabled: boolean;
  requiredOverride: boolean | null;
}

export const FIELD_RENDERER_BY_SOURCE_TYPE: Readonly<
  Record<SupportFieldSourceType, TicketFieldRendererType>
> = {
  default_requester: 'lookup',
  default_subject: 'single_line_text',
  default_source: 'dropdown',
  default_status: 'dropdown',
  default_priority: 'dropdown',
  default_ticket_type: 'dropdown',
  default_group: 'lookup',
  default_agent: 'lookup',
  default_internal_group: 'lookup',
  default_internal_agent: 'lookup',
  default_description: 'multi_line_text',
  default_company: 'lookup',
  default_source_info: 'single_line_text',
  custom_text: 'single_line_text',
  custom_paragraph: 'multi_line_text',
  custom_dropdown: 'dropdown',
  nested_field: 'dependent',
};

export function rendererTypeForSourceType(sourceType: SupportFieldSourceType): TicketFieldRendererType {
  return FIELD_RENDERER_BY_SOURCE_TYPE[sourceType];
}

export function orderTicketFields(fields: readonly TicketFieldDefinition[]): TicketFieldDefinition[] {
  return fields
    .map((field, sourceIndex) => ({ field, sourceIndex }))
    .sort(
      (left, right) =>
        left.field.position - right.field.position ||
        left.field.sourceOrder - right.field.sourceOrder ||
        left.sourceIndex - right.sourceIndex,
    )
    .map(({ field }) => field);
}
